import { Alert, Button, DialogActions, DialogContent } from "@mui/material";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Form } from "react-final-form";
import { Text } from "../../../common/components/input/Text";
import { Loading } from "../../../common/components/Loading";
import { useDataRequest } from "../../../common/hooks/api";
import { DataRequestDetail, NetworkRequest } from "../../../common/models";
import { Api } from "../../../common/service/Api";
import { DATE_FORMAT_MEDIUM } from "../../formatters/date";
import { requiredString } from "../../util/validation";
import { Callout, CalloutVariant } from "../Callout";
import { DisableableButton } from "../Disableable";

type ExtendDataRequestProps = {
  onSubmit: (values: any) => void;
  onClose: () => void;
  request: DataRequestDetail;
};

export const ExtendDataRequest = ({ onSubmit, request }: ExtendDataRequestProps) => {
  const localSubmit = (values) => {
    const dateValues = {
      requestDate: values.requestDate ? moment(values.requestDate).format() : undefined,
      dueDate: values.dueDate ? moment(values.dueDate).format() : undefined,
    };
    onSubmit(Object.assign({}, values, dateValues));
  };

  return (
    <Form
      onSubmit={localSubmit}
      initialValues={{
        notice: "",
      }}
      validate={validation}
      render={({ handleSubmit, ...rest }) => {
        return (
          <form onSubmit={handleSubmit}>
            <ExtendDataRequestForm {...rest} request={request} />
          </form>
        );
      }}
    />
  );
};

const validation = (values) => {
  const errors = {} as any;

  requiredString(values, "notice", errors);

  return errors;
};

const ExtendDataRequestForm = ({ request }) => {
  const orgId = request?.organizationId;
  const recipientName = request?.subjectFirstName;
  const requestType = request?.dataRequestType?.label ?? "request";
  const {
    dueDate,
    dataRequestType: { extendDueDateDays, maxDaysOpen },
  } = request;
  const newDueDate = moment(dueDate).add(extendDueDateDays, "days");
  const recipientEmail = request?.subjectEmailAddress;
  const requestId = request?.id;
  const extensionReasonPlaceholder =
    "Provide reason for extension here. For example: We require additional time in order to verify your request.";

  const { result: sample, request: extendDateTemplateRequest } = useDataRequest({
    queryKey: ["emailTemplate", orgId, "extend-due-date"],
    api: () => Api.organization.getOrganizationMessageSample(orgId, "extend-due-date"),
  });

  const { templateHTML } = sample ?? {};
  const [beforeHTML, setBeforeHTML] = useState("");
  const [afterHTML, setAfterHTML] = useState("");

  useEffect(() => {
    const replaceText = (match) => {
      if (match == "{first}") {
        return recipientName || "";
      } else if (match == "{type}") {
        return requestType;
      } else if (match == "{extensionDays}") {
        return extendDueDateDays;
      } else if (match == "{maxDaysOpen}") {
        return maxDaysOpen;
      }
    };

    if (templateHTML?.includes("[[[CUSTOM_TEXT]]]")) {
      const [before, after] = templateHTML
        .split('<p class="custom-text">[[[CUSTOM_TEXT]]]</p>')
        .map((t) => t.replace(/href/gi, "xhref"));

      const withTokens = before.replace(
        /{first}|{type}|{extensionDays}|{maxDaysOpen}/g,
        replaceText,
      );
      // Remove extra spacing when no first name is available (e.g. "Hello ," -> "Hello,")
      setBeforeHTML(withTokens.replace(/Hello\s+,/g, "Hello,"));
      setAfterHTML(after);
    } else {
      setBeforeHTML("");
      setAfterHTML("");
    }
  }, [templateHTML, recipientName, requestType]);

  const [disabled, setDisabled] = useState(true);
  const handleChange = (e: any) => {
    if (e.target?.value.length > 0) {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  };
  const buttonText = "Extend and Notify";

  return (
    <>
      <DialogContent className="py-0">
        {NetworkRequest.isFinished(extendDateTemplateRequest) ? (
          extendDateTemplateRequest.success ? (
            <>
              <p className="mb-sm">
                You may extend the due date once. Your new due date will be:{" "}
                <span className="text-weight-bold">{newDueDate.format(DATE_FORMAT_MEDIUM)}</span>
              </p>
              <p>
                The message below will be send to the requester. Fill in the reason in the suggested
                field:
              </p>
              <Callout variant={CalloutVariant.Gray} className="mb-md">
                <p>
                  <strong>To: {recipientEmail}</strong>
                </p>
                <p>
                  <strong>Subject: A message about your request {requestId}</strong>
                </p>
                <div dangerouslySetInnerHTML={{ __html: beforeHTML }} />
                <Text
                  isMultiLine={true}
                  field="notice"
                  name="notice"
                  label="Reason for extension"
                  placeholder={extensionReasonPlaceholder}
                  onChange={handleChange}
                  required
                />
                <div dangerouslySetInnerHTML={{ __html: afterHTML }} />
              </Callout>
            </>
          ) : (
            <Alert severity="error">There was an error loading the template.</Alert>
          )
        ) : (
          <Loading />
        )}
      </DialogContent>
      <DialogActions className="border-none p-lg">
        {disabled ? (
          <DisableableButton
            type="submit"
            color="primary"
            variant="contained"
            disabled={disabled}
            disabledTooltip="You must fill in a reason for due date extension."
          >
            {buttonText}
          </DisableableButton>
        ) : (
          <Button type="submit" color="primary" variant="contained">
            {buttonText}
          </Button>
        )}
      </DialogActions>
    </>
  );
};
